import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Store, MessageCircle, Package } from "lucide-react";

export default function SellerShop() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/sellers/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center text-neutral-500">
        Loading shop…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center">
        <h1 className="font-display font-black text-4xl">Shop not found</h1>
        <p className="mt-4 text-neutral-600">This seller doesn't exist or has no public storefront yet.</p>
        <Link to="/products" className="inline-flex mt-8 bg-[#0033FF] hover:bg-[#0022CC] text-white px-6 h-12 items-center font-semibold">
          Browse the marketplace
        </Link>
      </div>
    );
  }

  const { seller, products, totals } = data;
  const shopName = seller.shop_name || seller.name || "Independent shop";
  const waLink = seller.whatsapp ? `https://wa.me/${seller.whatsapp.replace(/[^\d]/g, "")}` : null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      {/* Storefront header */}
      <div className="border border-black bg-neutral-50 p-6 sm:p-10" data-testid="seller-shop-header">
        <div className="flex flex-wrap items-start gap-5">
          {seller.avatar_url ? (
            <img src={seller.avatar_url} alt={shopName} className="w-16 h-16 rounded-full object-cover border border-black" />
          ) : (
            <div className="w-16 h-16 bg-[#FF2A00] text-white grid place-items-center font-display font-black text-2xl">
              {shopName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#0033FF] font-bold mb-1 flex items-center gap-1">
              <Store className="w-3 h-3" /> Independent shop
            </div>
            <h1 data-testid="seller-shop-name" className="font-display font-black text-3xl sm:text-5xl tracking-tight text-[#FF2A00]">
              {shopName}
            </h1>
            {seller.shop_tagline && (
              <p className="mt-2 text-sm text-neutral-700 max-w-2xl">{seller.shop_tagline}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-neutral-600">
              <span className="inline-flex items-center gap-1"><Package className="w-3 h-3" /> {totals.products} product{totals.products === 1 ? "" : "s"}</span>
              <span>· {totals.in_stock} in stock</span>
            </div>
          </div>
          {waLink && (
            <a
              data-testid="seller-shop-whatsapp"
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb558] text-white px-4 h-11 font-semibold transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
        </div>
      </div>

      {/* Products grid */}
      <div className="mt-10">
        {products.length === 0 ? (
          <div className="border border-neutral-200 p-12 text-center text-neutral-500" data-testid="seller-shop-empty">
            This shop hasn't listed any products yet.
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">All items</h2>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                Newest first
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((p, i) => (
                <ProductCard key={p.product_id} product={p} index={i} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
